import React, { useState } from 'react'
import { ActivityIndicator, Linking, StyleSheet, TouchableOpacity, View } from 'react-native'
import { useTheme } from '@react-navigation/native'
import { Button, Icon, SearchBar, Text } from '@components'
import { useLanguage } from '@context'
import { Theme } from '@theme'
import { formatEventPrice, formatShortDate, showSuccessToast } from '@utils'
import {
  type CheckInFilter,
  type OrganizerAttendee,
  useCheckInAttendee,
  useExportEventAttendees,
  useOrganizerAttendees,
  useScreen,
  useSendEventTickets,
} from '@hooks'
import { EventAvatar } from '../../../events/components/EventAvatar'

const FILTERS: CheckInFilter[] = ['all', 'present', 'absent']

/** The organizer's CRM: search, entrance control and CSV export. */
export function AttendeesPanel({ eventId }: { eventId: string }) {
  const theme = useTheme()
  const { t, currentLanguage } = useLanguage()
  const { isMobile } = useScreen()
  const styles = getStyles(theme)
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState<CheckInFilter>('all')
  const { data, isLoading } = useOrganizerAttendees(eventId, search, filter)
  const checkIn = useCheckInAttendee()
  const { exportAttendees, isExporting } = useExportEventAttendees()
  const sendTicket = useSendEventTickets()
  const [pendingId, setPendingId] = useState<string | null>(null)
  const [sendingTicketId, setSendingTicketId] = useState<string | null>(null)

  const filterLabel: Record<CheckInFilter, string> = {
    all: t('events.manage.attendees.filterAll'),
    present: t('events.manage.attendees.filterPresent'),
    absent: t('events.manage.attendees.filterAbsent'),
  }

  const toggle = (attendee: OrganizerAttendee) => {
    // An unregistered person has no seat to tick.
    if (!attendee.attendeeId) return
    setPendingId(attendee.attendeeId)
    checkIn.mutate(
      { eventId, attendeeId: attendee.attendeeId, checkedIn: !attendee.checkedInAt },
      { onSettled: () => setPendingId(null) },
    )
  }

  const resendTicket = (attendee: OrganizerAttendee) => {
    if (!attendee.attendeeId) return
    setSendingTicketId(attendee.attendeeId)
    sendTicket.mutate(
      { eventId, recipients: 'one', attendeeId: attendee.attendeeId },
      {
        onSuccess: () =>
          showSuccessToast(t('events.manage.attendees.ticketSentTitle'), t('events.manage.attendees.ticketSent')),
        onSettled: () => setSendingTicketId(null),
      },
    )
  }

  const rows = data?.data ?? []
  const panelTitle = `${t('events.manage.attendees.title')} · ${data?.metadata.totals.total ?? 0}`
  const isPaidEvent = data?.metadata.priceCents !== null && data?.metadata.priceCents !== undefined

  return (
    <View style={styles.panel}>
      <View style={styles.head}>
        <Text type="titleBold">{panelTitle}</Text>
        <Button
          title={t('events.manage.attendees.export')}
          iconName="download"
          isSmall
          unfilled
          disabled={isExporting || (data?.metadata.totals.total ?? 0) === 0}
          onPress={() => exportAttendees(eventId)}
        />
      </View>

      {isPaidEvent ? (
        <View style={styles.collected} testID="crm-collected">
          <Icon name="check" size={16} color={theme.colors.green} />
          <Text type="bodySmallBold" color={theme.colors.green} style={styles.gapLeft}>
            {t('events.manage.attendees.collected', {
              amount: formatEventPrice(
                data?.metadata.collected.totalCents ?? 0,
                currentLanguage,
                data?.metadata.currency,
              ),
              commission: formatEventPrice(
                data?.metadata.collected.commissionCents ?? 0,
                currentLanguage,
                data?.metadata.currency,
              ),
              net: formatEventPrice(
                data?.metadata.collected.organizerNetCents ?? 0,
                currentLanguage,
                data?.metadata.currency,
              ),
              count: data?.metadata.collected.paidCount ?? 0,
            })}
          </Text>
        </View>
      ) : null}

      <View style={[styles.tools, isMobile && styles.toolsStacked]}>
        <SearchBar value={search} onChangeText={setSearch} containerStyle={styles.search} />
        <View style={styles.filters}>
          {FILTERS.map((value) => (
            <TouchableOpacity
              key={value}
              accessibilityRole="button"
              accessibilityState={{ selected: filter === value }}
              onPress={() => setFilter(value)}
              style={[styles.filter, filter === value && styles.filterActive]}
            >
              <Text
                type="bodySmallBold"
                color={filter === value ? theme.colors.white : theme.colors.secondary}
              >
                {filterLabel[value]}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
      </View>

      {isLoading ? (
        <ActivityIndicator color={theme.colors.primary} />
      ) : rows.length === 0 ? (
        <Text type="body" color={theme.colors.greyBlue} style={styles.empty}>
          {search || filter !== 'all'
            ? t('events.manage.attendees.emptySearch')
            : t('events.manage.attendees.empty')}
        </Text>
      ) : (
        <View>
          {rows.map((attendee) => (
            <AttendeeRow
              key={attendee.attendeeId ?? attendee.accountId}
              attendee={attendee}
              compact={isMobile}
              busy={pendingId === attendee.attendeeId}
              onToggle={() => toggle(attendee)}
              sendingTicket={sendingTicketId === attendee.attendeeId}
              onSendTicket={() => resendTicket(attendee)}
            />
          ))}
        </View>
      )}
    </View>
  )
}

interface AttendeeRowProps {
  attendee: OrganizerAttendee
  compact: boolean
  busy: boolean
  onToggle: () => void
  sendingTicket: boolean
  onSendTicket: () => void
}

function AttendeeRow({ attendee, compact, busy, onToggle, sendingTicket, onSendTicket }: AttendeeRowProps) {
  const theme = useTheme()
  const { t, currentLanguage } = useLanguage()
  const styles = getStyles(theme)
  const present = Boolean(attendee.checkedInAt)
  const fullName = `${attendee.firstName} ${attendee.lastName}`
  const role = [attendee.jobTitle, attendee.company].filter(Boolean).join(' · ')

  return (
    <View style={[styles.row, present && styles.rowPresent]}>
      <EventAvatar
        firstName={attendee.firstName}
        lastName={attendee.lastName}
        avatarUrl={attendee.avatarUrl}
        size={44}
      />
      <View style={styles.identity}>
        <View style={styles.nameLine}>
          <Text type="bodyBold" numberOfLines={1}>
            {fullName}
          </Text>
          {attendee.linkedinUrl ? (
            <TouchableOpacity
              accessibilityRole="link"
              onPress={() => Linking.openURL(attendee.linkedinUrl as string)}
              style={styles.gapLeft}
            >
              <Icon name="linkedin" size={16} color={theme.colors.linkedin} />
            </TouchableOpacity>
          ) : null}
        </View>
        {role ? (
          <Text type="bodySmall" color={theme.colors.secondary} numberOfLines={1}>
            {role}
          </Text>
        ) : null}
        <Text type="bodyExtraSmall" color={theme.colors.greyBlue} numberOfLines={1}>
          {compact
            ? attendee.email
            : `${attendee.email} · ${t(`events.manage.attendees.source.${attendee.source}`)} · ${t(
                'events.manage.attendees.joinedOn',
                {
                  date: formatShortDate(attendee.joinedAt, currentLanguage),
                },
              )}`}
        </Text>
      </View>
      {attendee.unregistered ? (
        <View style={styles.unregisteredBadge} testID={`unregistered-badge-${attendee.accountId}`}>
          <Text type="bodyExtraSmallBold" color={theme.colors.orange}>
            {t('events.manage.attendees.unregistered')}
          </Text>
        </View>
      ) : attendee.payment ? (
        <View style={styles.paidBadge} testID={`paid-badge-${attendee.attendeeId}`}>
          <Text type="bodyExtraSmallBold" color={theme.colors.green}>
            {compact
              ? t('events.manage.attendees.paid')
              : `${t('events.manage.attendees.paid')} · ${formatEventPrice(
                  attendee.payment.amountCents,
                  currentLanguage,
                  attendee.payment.currency,
                )}`}
          </Text>
        </View>
      ) : null}
      {attendee.unregistered ? null : (
        <TouchableOpacity
          accessibilityRole="checkbox"
          accessibilityState={{ checked: present, busy }}
          accessibilityLabel={t('events.manage.attendees.checkIn')}
          testID={`check-in-${attendee.attendeeId}`}
          disabled={busy}
          onPress={onToggle}
          style={[styles.check, present && styles.checkOn, busy && styles.busy]}
        >
          <Icon
            name="check"
            size={16}
            color={present ? theme.colors.white : theme.colors.greyBlue}
          />
          {!compact ? (
            <Text
              type="bodySmallBold"
              color={present ? theme.colors.white : theme.colors.secondary}
              style={styles.gapLeft}
            >
              {present
                ? t('events.manage.attendees.checkedIn')
                : t('events.manage.attendees.checkIn')}
            </Text>
          ) : null}
        </TouchableOpacity>
      )}
      {attendee.unregistered ? null : (
        <TouchableOpacity
          accessibilityRole="button"
          accessibilityLabel={t('events.manage.attendees.sendTicket')}
          testID={`send-ticket-${attendee.attendeeId}`}
          disabled={sendingTicket}
          onPress={onSendTicket}
          style={[styles.ticketButton, sendingTicket && styles.busy]}
        >
          <Icon name="qrCode" size={18} color={theme.colors.primary} />
        </TouchableOpacity>
      )}
    </View>
  )
}

const getStyles = (theme: Theme) =>
  StyleSheet.create({
    panel: {
      backgroundColor: theme.colors.white,
      borderRadius: theme.border.jumbo,
      borderWidth: 1,
      borderColor: theme.colors.grey,
      padding: theme.spacing.xl,
      gap: theme.spacing.md,
    },
    head: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      flexWrap: 'wrap',
      gap: theme.spacing.sm,
    },
    tools: { flexDirection: 'row', alignItems: 'center', gap: theme.spacing.md, flexWrap: 'wrap' },
    toolsStacked: { flexDirection: 'column', alignItems: 'stretch' },
    search: { flex: 1, minWidth: 220 },
    filters: { flexDirection: 'row', gap: theme.spacing.xs },
    filter: {
      paddingHorizontal: theme.spacing.md,
      paddingVertical: theme.spacing.xs + 2,
      borderRadius: theme.border.jumbo,
      borderWidth: 1,
      borderColor: theme.colors.grey,
    },
    filterActive: { backgroundColor: theme.colors.primary, borderColor: theme.colors.primary },
    empty: { paddingVertical: theme.spacing.xl, textAlign: 'center' },
    row: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: theme.spacing.md,
      paddingVertical: theme.spacing.md,
      paddingHorizontal: theme.spacing.sm,
      borderBottomWidth: 1,
      borderBottomColor: theme.colors.grey,
    },
    rowPresent: { backgroundColor: theme.colors.successSoft },
    identity: { flex: 1, minWidth: 0, gap: 1 },
    nameLine: { flexDirection: 'row', alignItems: 'center' },
    gapLeft: { marginLeft: theme.spacing.xs },
    check: {
      flexDirection: 'row',
      alignItems: 'center',
      borderRadius: theme.border.jumbo,
      borderWidth: 1,
      borderColor: theme.colors.grey,
      paddingHorizontal: theme.spacing.md,
      paddingVertical: theme.spacing.xs + 2,
    },
    collected: {
      flexDirection: 'row',
      alignItems: 'center',
      alignSelf: 'flex-start',
      backgroundColor: theme.colors.successSoft,
      borderRadius: theme.border.jumbo,
      paddingHorizontal: theme.spacing.md,
      paddingVertical: theme.spacing.xs + 2,
    },
    paidBadge: {
      backgroundColor: theme.colors.successSoft,
      borderRadius: theme.border.jumbo,
      paddingHorizontal: theme.spacing.sm,
      paddingVertical: theme.spacing.xs,
    },
    unregisteredBadge: {
      backgroundColor: theme.colors.sidebar,
      borderRadius: theme.border.jumbo,
      paddingHorizontal: theme.spacing.sm,
      paddingVertical: theme.spacing.xs,
    },
    checkOn: { backgroundColor: theme.colors.green, borderColor: theme.colors.green },
    busy: { opacity: 0.5 },
    ticketButton: {
      width: 36,
      height: 36,
      borderRadius: theme.border.medium,
      borderWidth: 1,
      borderColor: theme.colors.grey,
      alignItems: 'center',
      justifyContent: 'center',
    },
  })
